import { CheckCircle, Video, MessageCircle } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

export default function MentorMatchInfo({
  mentor,
  matchConfirmed,
  onConfirm,
  onReject,
  onShowDetails,
  onSendMessage,
  onStartVideoCall,
  unreadCount
}) {
  const { t, i18n } = useTranslation();
  const [showDetails, setShowDetails] = useState(false);

  const isEn = (i18n.language || "de").toLowerCase().startsWith("en");
  const tx = (key, deFallback, enFallback) =>
    t(key, { defaultValue: isEn ? enFallback : deFallback });

  const profileDescription =
    (mentor?.profileDescription ?? mentor?.profile_description ?? "").toString().trim();

  // ✅ NEU: Avatar robust (mentor.avatar ODER mentor.avatarPath / mentor.avatar_path)
  const avatarSrc = (() => {
    if (!mentor) return "";
    if (mentor.avatar) return mentor.avatar;

    const p = mentor.avatarPath ?? mentor.avatar_path;
    if (!p) return "";

    if (/^https?:\/\//i.test(p)) return p;

    const base = process.env.REACT_APP_API_URL || "";
    return `${base}/uploads/${p}`;
  })();

  return (
    <div className="bg-gray-800 rounded-2xl shadow-xl px-8 py-8 min-h-[260px] flex flex-col items-center justify-center w-full">
      <div className="flex flex-col md:flex-row items-center justify-center gap-8 w-full">
        {/* Avatar und Score */}
        <div className="flex flex-col items-center min-w-[120px]">
          <div className="relative">
            <img
              src={avatarSrc}
              alt={tx("mentorMatchInfo.avatarAlt", "Profilbild", "Profile picture")}
              className="w-20 h-20 rounded-full border-4 border-white object-cover shadow"
              style={{ background: "#181e26" }}
            />
            <span
              className="absolute -bottom-2 right-0 text-xs font-bold px-2 py-0.5 bg-green-700 text-white rounded-full shadow border-2 border-gray-900"
              style={{ fontSize: "0.85rem" }}
            >
              {mentor.score ? `${Number(mentor.score).toFixed(1)}%` : ""}
            </span>
          </div>

          <div className="text-base font-bold text-white mt-2 text-center">{mentor.name}</div>
          <div className="text-sm text-gray-300 text-center">{mentor.title}</div>
          <div className="text-xs text-gray-400 text-center">{mentor.company}</div>

          {/* Bei bestätigtem Match Buttons anzeigen */}
          {matchConfirmed && (
            <div className="flex flex-col gap-2 w-full mt-4">
              <button
                onClick={() => onSendMessage(mentor)}
                className="bg-mentixxgreen text-black font-semibold py-2 px-6 rounded-full shadow hover:bg-green-400 transition flex items-center justify-center gap-2 relative"
              >
                <MessageCircle size={18} />{" "}
                {tx("mentorMatchInfo.sendMessage", "Nachricht senden", "Send message")}
                {unreadCount > 0 && (
                  <span className="absolute -top-2 -right-2 bg-red-600 text-white text-xs px-1.5 py-0.5 rounded-full">
                    {unreadCount}
                  </span>
                )}
              </button>

              <button
                onClick={onStartVideoCall}
                className="bg-blue-700 text-white font-semibold py-2 px-6 rounded-full shadow hover:bg-blue-500 transition flex items-center justify-center gap-2"
              >
                <Video size={18} />{" "}
                {tx("mentorMatchInfo.startVideoCall", "Video Call starten", "Start video call")}
              </button>
            </div>
          )}
        </div>

        {/* Hauptinhalt */}
        <div className="flex flex-col items-center flex-1 gap-2">
          <div className="flex items-center gap-2 mb-2">
            <CheckCircle className="text-mentixxgreen" size={24} />
            <span className="font-semibold text-mentixxgreen text-lg">
              {tx("mentorMatchInfo.superMatch", "Super Match!", "Great match!")}
            </span>
          </div>

          <div className="text-xl text-white font-light text-center mb-1">
            {tx(
              "mentorMatchInfo.mentorGuidesYou",
              "Dein Mentor begleitet dich auf deinem Weg.",
              "Your mentor will guide you on your journey."
            )}
          </div>

          <div className="text-gray-400 text-sm mb-4 text-center">
            {tx("mentorMatchInfo.experience", "Erfahrung", "Experience")}:{" "}
            <span className="font-medium">{mentor.experience}</span>
          </div>

          {/* Buttons für Bestätigung und Details – nur anzeigen, solange noch NICHT bestätigt */}
          {!matchConfirmed && (
            <div className="flex flex-col md:flex-row w-full justify-center gap-4 mt-1">
              <button
                onClick={onConfirm}
                className="flex-1 py-2 bg-mentixxgreen text-black font-semibold rounded-full text-base hover:bg-green-400 transition"
              >
                {tx("mentorMatchInfo.firstStep", "Match bestätigen", "Confirm match")}
              </button>

              <button
                onClick={onReject}
                className="flex-1 py-2 border border-red-500 text-red-500 rounded-full text-base hover:bg-red-800/20 transition"
              >
                {tx("mentorMatchInfo.rejectMatch", "Match ablehnen", "Reject match")}
              </button>

              <button
                onClick={() => setShowDetails(true)}
                className="flex-1 py-2 border border-gray-500 text-gray-200 rounded-full text-base hover:bg-gray-700 transition"
              >
                {tx("mentorMatchInfo.moreAboutMentor", "Mehr über deinen Mentor", "More about your mentor")}
              </button>
            </div>
          )}

          {/* Falls bestätigt, zeige nur den "Mehr über..." Button */}
          {matchConfirmed && (
            <div className="flex flex-col md:flex-row w-full justify-center gap-4 mt-1">
              <button
                onClick={() => setShowDetails(true)}
                className="flex-1 py-2 border border-gray-500 text-gray-200 rounded-full text-base hover:bg-gray-700 transition"
              >
                {tx("mentorMatchInfo.moreAboutMentor", "Mehr über deinen Mentor", "More about your mentor")}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Modal für Kurzprofil + Ziele/Kompetenzen */}
      {showDetails && (
        <div className="fixed inset-0 z-40 bg-black bg-opacity-60 flex items-center justify-center">
          <div className="bg-[#232931] rounded-2xl p-8 w-full max-w-lg shadow-2xl">
            <div className="mb-3 flex items-center gap-2">
              <span className="text-mentixxgreen text-xl font-bold">
                {tx("mentorMatchInfo.moreAboutMentor", "Mehr über deinen Mentor", "More about your mentor")}
              </span>
              <button
                className="ml-auto text-gray-400 hover:text-white text-xl"
                onClick={() => setShowDetails(false)}
                aria-label={tx("mentorMatchInfo.close", "Schließen", "Close")}
              >
                ×
              </button>
            </div>

            {/* Kurzprofil – NUR im Popup */}
            <div className="mb-5">
              <div className="text-base text-gray-200 font-semibold mb-1">
                {tx("mentorMatchInfo.shortProfile", "Kurzprofil", "Short profile")}:
              </div>
              <div className="text-sm text-gray-300 whitespace-pre-line">
                {profileDescription || "–"}
              </div>
            </div>

            {mentor.surveyId === 28 ? (
              <>
                <div className="mb-4">
                  <div className="text-base text-gray-200 font-semibold mb-1">
                    {tx("mentorMatchInfo.applicationFields", "Anwendungsfelder", "Fields of Application")}:
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {mentor.applicationFields?.length ? (
                      mentor.applicationFields.map((f, i) => (
                        <span key={i} className="bg-green-700/70 text-green-200 px-3 py-1 rounded-full text-xs">
                          {f}
                        </span>
                      ))
                    ) : (
                      <span className="text-gray-400">–</span>
                    )}
                  </div>
                </div>
                <div className="mb-1">
                  <div className="text-base text-gray-200 font-semibold mb-1">
                    {tx("mentorMatchInfo.methodsSkills", "Methoden & Skills", "Methods & Skills")}:
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {mentor.methodsSkills?.length ? (
                      mentor.methodsSkills.map((s, i) => (
                        <span key={i} className="bg-blue-800/60 text-blue-200 px-3 py-1 rounded-full text-xs">
                          {s}
                        </span>
                      ))
                    ) : (
                      <span className="text-gray-400">–</span>
                    )}
                  </div>
                </div>
              </>
            ) : (
              <>
                <div className="mb-4">
                  <div className="text-base text-gray-200 font-semibold mb-1">
                    {tx("mentorMatchInfo.sharedGoals", "Gemeinsame Ziele", "Shared goals")}:
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {mentor.sharedGoals?.length ? (
                      mentor.sharedGoals.map((g, i) => (
                        <span key={i} className="bg-green-700/70 text-green-200 px-3 py-1 rounded-full text-xs">
                          {g}
                        </span>
                      ))
                    ) : (
                      <span className="text-gray-400">–</span>
                    )}
                  </div>
                </div>
                <div className="mb-1">
                  <div className="text-base text-gray-200 font-semibold mb-1">
                    {tx("mentorMatchInfo.sharedCompetences", "Gemeinsame Kompetenzen", "Shared competences")}:
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {mentor.sharedCompetences?.length ? (
                      mentor.sharedCompetences.map((c, i) => (
                        <span key={i} className="bg-blue-800/60 text-blue-200 px-3 py-1 rounded-full text-xs">
                          {c}
                        </span>
                      ))
                    ) : (
                      <span className="text-gray-400">–</span>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
